<template>
  <SfDropdownMultiselect
    v-model="tags"
    data-testid="tags-filter-dropdown"
    :options="options"
    dropdown-id="tagsDropdownFilter"
    :placeholder="I18n.t('inbox.filters.tags.search_placeholder')"
    :trigger-label="I18n.t('inbox.filters.trigger_labels.tags')"
    :trigger-label-custom-suffix="triggerSuffix"
  >
    <template #before>
      <SfDropdownItemGroup :with-separator="true">
        <SfDropdownItem
          class="tw-leading-3"
          :has-checkmark="tags.length === 0"
          :has-border="false"
          :class="{ 'tw-font-medium': tags.length === 0 }"
          @click="clearSelected"
        >
          {{ allLabel }}
        </SfDropdownItem>

        <SfDropdownItem
          class="tw-leading-3"
          :has-checkmark="isUntaggedSelected"
          :has-border="false"
          :class="{ 'tw-font-medium': isUntaggedSelected }"
          @click="selectUntagged"
        >
          {{ noneLabel }}
        </SfDropdownItem>
      </SfDropdownItemGroup>
    </template>
  </SfDropdownMultiselect>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useInboxFiltersStore } from '../store/inbox-filters'
import SfDropdownMultiselect from '@app/shared/vue/components/sf-dropdown/sf-dropdown-multiselect.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { storeToRefs } from 'pinia'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import SfDropdownItemGroup from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item-group.vue'
import { UNTAGGED_FILTER } from '../store/constants'
import { useDropdown } from '@app/shared/pinia/dropdown'
import type { OptionGroup } from '@app/shared/vue/components/sf-dropdown/types'

interface Props {
  options: OptionGroup[]
}

const I18n = useI18n()
defineProps<Props>()
const inboxFiltersStore = useInboxFiltersStore()
const { tags } = storeToRefs(inboxFiltersStore)
const allLabel = I18n.t('inbox.filters.tags.labels.any')
const noneLabel = I18n.t('inbox.filters.tags.labels.none')
const dropdownStore = useDropdown()

const triggerSuffix = computed(() => {
  if (tags.value.length === 0) {
    return allLabel
  }

  if (tags.value.includes(UNTAGGED_FILTER)) {
    return noneLabel
  }

  return undefined
})

function clearSelected(): void {
  tags.value = []
  dropdownStore.toggleDropdown('tagsDropdownFilter')
}

const isUntaggedSelected = computed(() => {
  return tags.value.includes(UNTAGGED_FILTER)
})

function selectUntagged(): void {
  tags.value = [UNTAGGED_FILTER]
  dropdownStore.toggleDropdown('tagsDropdownFilter')
}

watch(
  tags,
  (newTags) => {
    if (newTags.includes(UNTAGGED_FILTER) && newTags.length > 1) {
      tags.value = newTags.filter((t) => t !== UNTAGGED_FILTER)
    }
  },
  { deep: true }
)
</script>
